'use client';

import { useState, type ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
  Globe02Icon,
  SourceCodeIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Collapsible } from '../../../components/collapsible';
import { IconButton } from '../../../components/icon-button';
import { MetaDefault } from '../../../components/typography';
import { ReadOnlyCodeEditor } from '../../chat/components/ReadOnlyCodeEditor';
import { Tooltip } from '../../../components/tooltip';

type UnverifiedAssetProbeExecutionCardProps = {
  title: string;
  code?: string | null;
  result?: string | null;
  showHttpView: boolean;
  onShowCode: () => void;
  onShowHttp: () => void;
  canToggleHttp: boolean;
  hasError: boolean;
  children?: ReactNode;
};

function ProbeCodeResultSection({
  label,
  value,
  showBottomBorder,
  showLineNumbers,
}: {
  label: string;
  value: string;
  showBottomBorder: boolean;
  showLineNumbers?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      lazyMount
      unmountOnExit
    >
      <Box
        bg="transparent"
        borderBottomWidth={showBottomBorder ? '1px' : '0'}
        borderBottomColor="border.neutral.muted"
        width="100%"
      >
        <Collapsible.Trigger asChild>
          <HStack
            px={3}
            h="32px"
            cursor="pointer"
            width="100%"
            gap={1.5}
            alignItems="center"
          >
            <Box display="flex" alignItems="center" color="fg.muted">
              {isOpen ? (
                <ArrowDown01Icon size={10} />
              ) : (
                <ArrowRight01Icon size={10} />
              )}
            </Box>
            <MetaDefault color="fg.muted">{label}</MetaDefault>
          </HStack>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Box
            borderTopWidth="1px"
            borderColor="border.neutral.muted"
            bg="transparent"
            maxH="300px"
            overflow="hidden"
          >
            <ReadOnlyCodeEditor
              value={value}
              maxHeight="300px"
              minHeight="44px"
              showLineNumbers={showLineNumbers}
              language="json"
            />
          </Box>
        </Collapsible.Content>
      </Box>
    </Collapsible.Root>
  );
}

function ProbeCodeResultSections({
  code,
  result,
}: {
  code?: string | null;
  result?: string | null;
}) {
  const t = useTranslations('designSystem.applicationAssets');
  const hasCode = Boolean(code?.trim());
  const hasResult = Boolean(result?.trim());

  if (!hasCode && !hasResult) {
    return null;
  }

  return (
    <VStack alignItems="stretch" gap={0} width="100%">
      {hasCode ? (
        <ProbeCodeResultSection
          label={t('probeTraceCode')}
          value={code ?? ''}
          showBottomBorder={hasResult}
          showLineNumbers
        />
      ) : null}
      {hasResult ? (
        <ProbeCodeResultSection
          label={t('probeTraceResult')}
          value={result ?? ''}
          showBottomBorder={false}
        />
      ) : null}
    </VStack>
  );
}

export function UnverifiedAssetProbeExecutionCard({
  title,
  code,
  result,
  showHttpView,
  onShowCode,
  onShowHttp,
  canToggleHttp,
  hasError,
  children,
}: UnverifiedAssetProbeExecutionCardProps) {
  const t = useTranslations('designSystem.artifacts');

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="transparent"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <HStack gap={1.5} alignItems="center">
          <MetaDefault color="fg.default">{title}</MetaDefault>
          {hasError ? (
            <Box color="fg.error" display="flex" alignItems="center">
              <Cancel01Icon size={10} />
            </Box>
          ) : null}
        </HStack>
        {canToggleHttp ? (
          <HStack
            gap="0"
            borderWidth="1px"
            borderColor="border.neutral.muted"
            borderRadius="sm"
            overflow="hidden"
            flexShrink={0}
          >
            <Tooltip title={t('showCode')}>
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={t('showCode')}
                stopPropagation
                onClick={onShowCode}
                h="5"
                w="5"
                minW="5"
                borderRadius="0"
                bg={!showHttpView ? 'bg.muted' : 'transparent'}
                color={!showHttpView ? 'fg.default' : 'fg.muted'}
                _hover={{
                  bg: !showHttpView ? 'bg.muted' : 'bg.hover',
                }}
              >
                <SourceCodeIcon size={10} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('showHttpRequests')}>
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={t('showHttpRequests')}
                stopPropagation
                onClick={onShowHttp}
                h="5"
                w="5"
                minW="5"
                borderRadius="0"
                bg={showHttpView ? 'bg.muted' : 'transparent'}
                color={showHttpView ? 'fg.default' : 'fg.muted'}
                _hover={{
                  bg: showHttpView ? 'bg.muted' : 'bg.hover',
                }}
              >
                <Globe02Icon size={10} />
              </IconButton>
            </Tooltip>
          </HStack>
        ) : null}
      </HStack>
      {showHttpView && children ? (
        children
      ) : (
        <ProbeCodeResultSections code={code} result={result} />
      )}
    </Box>
  );
}
